import React, { useContext } from 'react';
import { type MapDispatchToProps, type MapStateToProps, connect } from 'react-redux';

import * as BackstageConstants from '../../constants';
import * as D from '../../deps';
import { getUserStationId } from '../../utils';

import { getRadioOnlyModalConfig } from './ArtistRadioOnlyModal';

export interface IArtistButtonControlsStateProps {
  artistPlayId?: string;
  shareableUrlPath?: string;
  isArtistTopSongsPlaying?: boolean;
  isArtistStationPlaying?: boolean;
  nowPlayingUrl?: ReturnType<typeof D.getStationPlayPathForStationCode>;
  topSongsHaveNoRights?: boolean;
  topSongsLooksDisabled?: boolean;
  topSongsIsDisabled?: boolean;
  hasRadio?: boolean;
}

export interface IArtistButtonControlsDispatchProps {
  openModal: typeof D.openModal;
  closeModal: typeof D.closeModal;
  openShareModal: typeof D.Actions.Share.openShareModal;
  pauseTrack: typeof D.Actions.UseCases.pauseTrack;
  startStation: typeof D.Actions.UseCases.startStation;
  playResumeSource: typeof D.playResumeSource;
}

export interface IArtistButtonControlsOwnProps {
  pandoraId: string;
  nowPlayingUrl: string;
  hasAnnotatedTopSongs?: boolean;
  hasRadio?: boolean;
}

export type IArtistButtonControlsProps = IArtistButtonControlsStateProps &
  IArtistButtonControlsDispatchProps &
  IArtistButtonControlsOwnProps;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useClickControls(props: IArtistButtonControlsProps) {
  const { integration, router } = useContext(D.AppContext);
  const {
    pandoraId,
    artistPlayId,
    shareableUrlPath,
    hasRadio,
    isArtistStationPlaying,
    nowPlayingUrl,
    topSongsHaveNoRights,
    openModal,
    closeModal,
    openShareModal,
    pauseTrack,
    startStation,
    playResumeSource,
  } = props;

  // @ts-expect-error TS(7006) FIXME: Parameter 'componentEventType' implicitly has an '... Remove this comment to see the full error message
  const logClickEvent = (componentEventType): void => {
    integration.action.clicked({
      pandoraId,
      componentEventType,
      contentSection: BackstageConstants.BackstageSections.CONTROL_BAR,
    });
  };

  const playArtistStation = (): void => {
    logClickEvent(BackstageConstants.BackstageEvents.startArtistStation);
    if (isArtistStationPlaying) {
      // TODO: WEBCLIENT-10081 This might be better in the startStation use case, if we want clicking play on
      // a playing artist station to always route to NP
      // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
      router.push(nowPlayingUrl);
    } else {
      // TODO: WEBCA-2936 investigate why we're only passing these props through
      // to START_STATION.
      startStation({
        pandoraId,
        rerouteBasedOnTier: true,
        componentName: 'BackstageArtist',
        // TODO: We're currently passing undefined to this actions param should mapStateToProps return {}
        // @ts-expect-error TS(2322) Type 'string | undefined' is not assignable to type 'string'....
        pageUrl: shareableUrlPath,
      });
    }
  };

  const onModalStartStationClick = (): void => {
    closeModal();
    playArtistStation();
  };

  const pauseTopSongs = (): void => {
    logClickEvent(BackstageConstants.BackstageEvents.pause);
    pauseTrack();
  };

  const playTopSongs = (): void => {
    if (topSongsHaveNoRights) {
      logClickEvent(BackstageConstants.BackstageEvents.insufficientRights);
      openModal(getRadioOnlyModalConfig({ hasRadio, onModalStartStationClick }));
      return;
    }
    logClickEvent(BackstageConstants.BackstageEvents.play);
    // TODO: WEBCA-2934 investigate why we're only passing these props through
    // to PLAY_RESUME_SOURCE.
    // @ts-expect-error
    playResumeSource({
      id: artistPlayId,
      pandoraType: D.PandoraTypes.ARTIST_TOP_SONGS,
      context: D.Constants.Ads.Rewards.ContextTypes.BACKSTAGE,
      pageUrl: shareableUrlPath,
    });
  };

  const shareArtist = (): void => {
    logClickEvent(BackstageConstants.BackstageEvents.share);
    openShareModal(null, {
      shareParentSourceId: pandoraId,
      shareParentSourceType: D.Constants.SourceTypes.PandoraTypes.ARTIST,
    });
  };

  return {
    pauseTopSongs,
    playArtistStation,
    playTopSongs,
    shareArtist,
    topSongsHaveNoRights,
  };
}

export const mapStateToProps: MapStateToProps<
  IArtistButtonControlsStateProps,
  IArtistButtonControlsOwnProps,
  $TSFixMe
> = (state, ownProps) => {
  const isCuratorExperienceEnabled = D.Utils.TunerVars.curatorExperienceEnabled(state);
  if (isCuratorExperienceEnabled) {
    return {};
  }
  const { pandoraId } = ownProps;
  const details = D.getInDomain('catalog.utils.getCatalogDetailsFromState')(state, pandoraId);
  const annotation = D.getAnnotationsById(state, pandoraId) || BackstageConstants.emptyMap;
  const isAudioPlaying = D.getInDomain('playback.utils.isAudioPlaying', false);
  const isPlaylistOn = D.getInDomain('playback.utils.isSourcePlayingCurrently', false);
  const artistPlayId = details.get('artistPlayId', '');
  const topTracks = details
    .get('topTracks', BackstageConstants.emptyList)
    .slice(0, BackstageConstants.BackstageConstants.NUM_TOP_TRACKS_TO_RENDER);
  const annotatedTopSongs = D.getManyAnnotationsById(state, topTracks);
  const hasAnnotatedTopSongs = annotatedTopSongs.some(annotatedTopSong =>
    D.hasInteractiveRights(annotatedTopSong),
  );
  const userStationId = getUserStationId(state, pandoraId);

  return {
    artistPlayId,
    hasRadio: ownProps.hasRadio ?? annotation.get('hasRadio', false),
    isArtistStationPlaying: userStationId && isPlaylistOn(state, userStationId),
    isArtistTopSongsPlaying: isAudioPlaying(state) && isPlaylistOn(state, artistPlayId),
    nowPlayingUrl: D.getStationPlayPathForStationCode(userStationId),
    shareableUrlPath: annotation.get('shareableUrlPath', ''),
    topSongsHaveNoRights: !hasAnnotatedTopSongs,
    topSongsIsDisabled: topTracks.isEmpty(),
    topSongsLooksDisabled: !topTracks.isEmpty() && !hasAnnotatedTopSongs,
  };
};

const mapDispatchToProps: MapDispatchToProps<
  IArtistButtonControlsDispatchProps,
  IArtistButtonControlsOwnProps
> = {
  closeModal: D.closeModal,
  openModal: D.openModal,
  openShareModal: D.Actions.Share.openShareModal,
  pauseTrack: D.Actions.UseCases.pauseTrack,
  playResumeSource: D.playResumeSource,
  startStation: D.Actions.UseCases.startStation,
};

export const withArtistButtonControls = (
  WrappedComponent: React.ComponentType<$TSFixMe>,
): $TSFixMe => {
  const ArtistButtonControls: React.FC<IArtistButtonControlsProps> = props => {
    const { isArtistTopSongsPlaying, topSongsLooksDisabled, topSongsIsDisabled } = props;

    const { playTopSongs, pauseTopSongs, playArtistStation, shareArtist } = useClickControls(props);

    return (
      <WrappedComponent
        playTopSongs={playTopSongs}
        pauseTopSongs={pauseTopSongs}
        isArtistTopSongsPlaying={isArtistTopSongsPlaying}
        topSongsLooksDisabled={topSongsLooksDisabled}
        topSongsIsDisabled={topSongsIsDisabled}
        playArtistStation={playArtistStation}
        shareArtist={shareArtist}
        {...props}
      />
    );
  };

  return connect(
    mapStateToProps,
    mapDispatchToProps,
    // @ts-ignore-error
  )(ArtistButtonControls);
};
